import { useCallback, useLayoutEffect, useRef, type KeyboardEvent } from 'react';

/**
 * Keyboard behaviour for a `role="radiogroup"` of `role="radio"` buttons (WAI-ARIA APG radio group):
 * Tab enters and leaves the group as ONE stop (on the checked radio, else the first enabled one),
 * Arrow keys move to the next/previous radio (wrapping), Home/End jump to the ends.
 *
 * Spread the returned props on the radiogroup element; the radios need no changes. Tab stops follow
 * `aria-checked` after every render, so a selection made by mouse moves the stop too.
 *
 * `selectOnFocus` (default true): moving with the arrows also selects, as the pattern prescribes.
 * Pass false where choosing a radio has an immediate side effect (e.g. it saves to the server), so
 * the arrows only move focus and Space/Enter confirms.
 *
 * 2026-10-02: the period chips, filter bars and chart switches across the app were radio groups
 * that Tab walked one button at a time and the arrows ignored; this hook gives them all the same
 * behaviour the callback filters already had.
 */
export function useRovingRadioGroup<T extends HTMLElement = HTMLDivElement>({
  selectOnFocus = true,
}: { selectOnFocus?: boolean } = {}) {
  const ref = useRef<T>(null);

  useLayoutEffect(() => {
    const group = ref.current;
    if (!group) return;
    const radios = ownRadios(group);
    if (radios.length === 0) return;
    const checked = radios.find((r) => r.getAttribute('aria-checked') === 'true' && !isDisabled(r));
    const stop = checked ?? radios.find((r) => !isDisabled(r));
    for (const r of radios) r.tabIndex = r === stop ? 0 : -1;
  });

  const onKeyDown = useCallback(
    (event: KeyboardEvent<T>) => {
      const group = ref.current;
      const target = event.target as HTMLElement;
      if (!group || target.getAttribute('role') !== 'radio') return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const radios = ownRadios(group).filter((r) => !isDisabled(r));
      const index = radios.indexOf(target);
      if (index === -1 || radios.length === 0) return;

      let next: number | null = null;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % radios.length;
      else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index - 1 + radios.length) % radios.length;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = radios.length - 1;
      if (next === null) return;

      event.preventDefault();
      event.stopPropagation();
      const radio = radios[next]!;
      for (const r of ownRadios(group)) r.tabIndex = r === radio ? 0 : -1;
      radio.focus();
      if (selectOnFocus && radio.getAttribute('aria-checked') !== 'true') radio.click();
    },
    [selectOnFocus],
  );

  return { ref, onKeyDown };
}

/** The group's own radios: not those of a radiogroup nested inside it. */
function ownRadios(group: HTMLElement): HTMLElement[] {
  return Array.from(group.querySelectorAll<HTMLElement>('[role="radio"]')).filter(
    (r) => r.closest('[role="radiogroup"]') === group,
  );
}

function isDisabled(el: HTMLElement): boolean {
  return el.hasAttribute('disabled') || el.getAttribute('aria-disabled') === 'true';
}
